package com.kanati.shared.consumer

import com.kanati.shared.commerce.PaymentMethod
import com.kanati.shared.ticket.TicketStatus
import kotlinx.serialization.Serializable

@Serializable
enum class ConsumerVerificationDelivery {
    INLINE_PILOT,
}

@Serializable
data class ConsumerAccountDto(
    val id: String,
    val districtId: String,
    val schoolId: String,
    val email: String,
    val createdAt: String,
)

@Serializable
data class RequestConsumerSessionCodeRequest(
    val schoolId: String,
    val email: String,
)

@Serializable
data class RequestConsumerSessionCodeResponse(
    val challengeId: String,
    val maskedEmail: String,
    val expiresAt: String,
    val delivery: ConsumerVerificationDelivery,
    val verificationCode: String? = null,
)

@Serializable
data class VerifyConsumerSessionCodeRequest(
    val challengeId: String,
    val verificationCode: String,
)

@Serializable
data class ConsumerSessionDto(
    val accessToken: String,
    val tokenType: String = "Bearer",
    val expiresAt: String,
    val consumer: ConsumerAccountDto,
)

@Serializable
enum class ConsumerEventAvailability {
    AVAILABLE,
    LIMITED,
    SOLD_OUT,
}

@Serializable
data class ConsumerEventSummaryDto(
    val id: String,
    val districtId: String,
    val schoolId: String,
    val name: String,
    val startDate: String,
    val endDate: String,
    val location: String,
    val startingPriceAmount: Int?,
    val availability: ConsumerEventAvailability,
    val remainingCount: Int? = null,
)

@Serializable
data class ConsumerTicketTierOfferDto(
    val id: String,
    val name: String,
    val priceAmount: Int,
    val availability: ConsumerEventAvailability,
    val remainingCount: Int? = null,
)

@Serializable
data class ConsumerEventDetailDto(
    val id: String,
    val districtId: String,
    val schoolId: String,
    val name: String,
    val startDate: String,
    val endDate: String,
    val location: String,
    val startingPriceAmount: Int?,
    val availability: ConsumerEventAvailability,
    val remainingCount: Int? = null,
    val ticketTiers: List<ConsumerTicketTierOfferDto>,
)

@Serializable
data class ConsumerEventListResponse(
    val schoolId: String,
    val events: List<ConsumerEventSummaryDto>,
)

@Serializable
data class ConsumerAppRuntimeSettings(
    val nativeUiThemeJson: String? = null,
    val appDocumentJson: String? = null,
)

@Serializable
data class ConsumerAppRuntimeConfigResponse(
    val districtId: String,
    val schoolId: String,
    val settings: ConsumerAppRuntimeSettings? = null,
)

@Serializable
enum class ConsumerPurchaseIntentStatus {
    PENDING_PAYMENT,
    COMPLETED,
    CANCELED,
    EXPIRED,
}

@Serializable
data class CreateConsumerPurchaseIntentLineItemRequest(
    val ticketTierId: String,
    val quantity: Int,
)

@Serializable
data class CreateConsumerPurchaseIntentRequest(
    val eventId: String,
    val lineItems: List<CreateConsumerPurchaseIntentLineItemRequest> = emptyList(),
    val purchaserName: String? = null,
    val paymentMethod: PaymentMethod = PaymentMethod.CARD,
) {
    constructor(
        eventId: String,
        ticketTierId: String,
        quantity: Int,
        purchaserName: String? = null,
        paymentMethod: PaymentMethod = PaymentMethod.CARD,
    ) : this(
        eventId = eventId,
        lineItems = listOf(
            CreateConsumerPurchaseIntentLineItemRequest(
                ticketTierId = ticketTierId,
                quantity = quantity,
            )
        ),
        purchaserName = purchaserName,
        paymentMethod = paymentMethod,
    )
}

@Serializable
data class ConsumerPurchaseIntentLineItemDto(
    val ticketTierId: String,
    val ticketTierName: String,
    val quantity: Int,
    val unitPriceAmount: Int,
    val lineSubtotalAmount: Long,
)

@Serializable
data class ConsumerPurchaseIntentDto(
    val id: String,
    val consumerId: String,
    val schoolId: String,
    val eventId: String,
    val eventName: String,
    val quantity: Int,
    val lineItems: List<ConsumerPurchaseIntentLineItemDto> = emptyList(),
    val subtotalAmount: Long,
    val totalAmount: Long,
    val paymentMethod: PaymentMethod,
    val status: ConsumerPurchaseIntentStatus,
    val purchaserName: String? = null,
    val paymentReference: String? = null,
    val createdAt: String,
    val expiresAt: String? = null,
    val completedAt: String? = null,
) {
    constructor(
        id: String,
        consumerId: String,
        schoolId: String,
        eventId: String,
        eventName: String,
        ticketTierId: String,
        ticketTierName: String,
        quantity: Int,
        unitPriceAmount: Int,
        subtotalAmount: Long,
        totalAmount: Long,
        paymentMethod: PaymentMethod,
        status: ConsumerPurchaseIntentStatus,
        purchaserName: String? = null,
        paymentReference: String? = null,
        createdAt: String,
        expiresAt: String? = null,
        completedAt: String? = null,
    ) : this(
        id = id,
        consumerId = consumerId,
        schoolId = schoolId,
        eventId = eventId,
        eventName = eventName,
        quantity = quantity,
        lineItems = listOf(
            ConsumerPurchaseIntentLineItemDto(
                ticketTierId = ticketTierId,
                ticketTierName = ticketTierName,
                quantity = quantity,
                unitPriceAmount = unitPriceAmount,
                lineSubtotalAmount = subtotalAmount,
            )
        ),
        subtotalAmount = subtotalAmount,
        totalAmount = totalAmount,
        paymentMethod = paymentMethod,
        status = status,
        purchaserName = purchaserName,
        paymentReference = paymentReference,
        createdAt = createdAt,
        expiresAt = expiresAt,
        completedAt = completedAt,
    )
}

@Serializable
data class CreateConsumerPurchaseIntentResponse(
    val purchase: ConsumerPurchaseIntentDto,
)

/**
 * Successful payment-start payload. Unavailable configuration remains a request/service error.
 * Wallet flags describe payment options, not overall card readiness or purchase completion.
 */
@Serializable
data class StartConsumerPurchaseCardPaymentResponse(
    val purchaseIntentId: String,
    val stripePaymentIntentId: String,
    val paymentIntentClientSecret: String,
    val publishableKey: String,
    val amount: Long,
    val currencyCode: String = "USD",
    val merchantDisplayName: String? = null,
    val googlePayEnabled: Boolean = false,
    val applePayEnabled: Boolean = false,
)

@Serializable
data class CompleteConsumerPurchaseIntentRequest(
    // Backward-compatible only. New clients should complete or refresh without echoing provider payment ids.
    val paymentReference: String? = null,
)

@Serializable
data class ConsumerWalletTicketSummaryDto(
    val id: String,
    val eventId: String,
    val eventName: String,
    val eventStartDate: String,
    val location: String,
    val ticketTierName: String,
    val status: TicketStatus,
    val holderName: String? = null,
    val createdAt: String,
)

@Serializable
data class ConsumerWalletTicketDetailDto(
    val id: String,
    val eventId: String,
    val eventName: String,
    val eventStartDate: String,
    val eventEndDate: String,
    val location: String,
    val ticketTierName: String,
    val ticketTierPriceAmount: Int,
    val status: TicketStatus,
    val holderName: String? = null,
    val ticketCode: String,
    val qrPayload: String,
    val createdAt: String,
    val usedAt: String? = null,
)

@Serializable
data class ConsumerWalletTicketsResponse(
    val tickets: List<ConsumerWalletTicketSummaryDto>,
)

@Serializable
data class CompleteConsumerPurchaseIntentResponse(
    val purchase: ConsumerPurchaseIntentDto,
    val tickets: List<ConsumerWalletTicketDetailDto>,
)
